import { useContext, useMemo } from "react";
import { DocumentationAreaContext } from "../providers/DocumentationAreaProvider";
import { AvailableActor } from "src/contexts/documentationArea/domain/AvailableActor";

export function useDocumentationArea() {
  const {
    documentationArea,
    actorBasicData,
    setIsDocumentationAreaQueryEnabled,
    selectedActorId,
    setSelectedActorId,
  } = useContext(DocumentationAreaContext);

  const actor = documentationArea?.getActor();
  const isCompany = documentationArea?.isForCompanies() ?? false;
  const companyLegalName = actor?.getIdentity()?.getRepresentedEntity()?.getLegalName() ?? null;
  const actorName = actor?.getName() ?? null;
  const rawAvailableActors = documentationArea?.getAvailableActors() ?? [];

  // When the API returns no availableActors (single-actor scenario where the
  // backend omits the list), synthesise an entry from the current actor so
  // that name display and readOnly derivation always have a source of truth.
  const availableActors: AvailableActor[] = useMemo(() => {
    if (rawAvailableActors.length > 0) return rawAvailableActors;
    if (!actor) return [];
    // Build full name: name + first surname + second surname
    const fullName = [actor.getName(), actor.getFirstSurname(), actor.getSecondSurname()]
      .filter(Boolean)
      .join(" ");
    return [
      {
        id: actor.getId(),
        readOnly: false,
        fullName: fullName || "",
      },
    ];
  }, [rawAvailableActors, actor]);

  // Derive readOnly from the matched entry in availableActors.
  const isReadOnly = availableActors.find((a) => a.id === selectedActorId)?.readOnly ?? false;
  const actorInvitation = documentationArea?.getActorInvitation() ?? null;

  return {
    documentationArea,
    actorBasicData,
    actor,
    actorInvitation,
    notifications: actor?.getNotifications() ?? [],
    payslips: actor?.getPayslipOrders() ?? [],
    models: actor?.getModelOrders() ?? [],
    identityVerifications: actor?.getIdentityVerificationOrders() ?? [],
    otherDocuments: actor?.getOtherDocumentOrders() ?? [],
    allModelsCompleted: actor?.areAllModelsCompleted() ?? false,
    setIsDocumentationAreaQueryEnabled,
    isAnyModelOrderProcessing: actor?.isAnyModelOrderProcessing() ?? false,
    headerName: isCompany ? companyLegalName : actorName,
    isCompany,
    representativeIdNumber: actor?.getRepresentativeIdNumber() ?? null,
    openAdminBanner: actor?.getOpenAdminBanner() ?? null,
    availableActors,
    selectedActorId,
    setSelectedActorId,
    isReadOnly,
  };
}
